import {
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Decimal } from "decimal.js";
import groupBy from "lodash/groupBy";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import DatePicker from "../../components/datePicker/DatePicker";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { CostCurrency } from "../costApi";
import type { CreatePaymentBundleRequestBody } from "./paymentsApi";

interface BasketItem {
  readonly costId: string;
  readonly bookingNumber: string;
  readonly provider: {
    readonly id: string;
    readonly name: string;
  };
  readonly currency: CostCurrency;
  readonly invoice: {
    readonly invoiceNumber: string;
    readonly amount?: Decimal;
  };
}

interface Props {
  readonly items: BasketItem[];
  readonly onCreateBundle: (request: CreatePaymentBundleRequestBody) => Promise<void>;
}

function invoiceKey(providerId: string, invoiceNumber: string) {
  return `${providerId}/${invoiceNumber}`;
}

function defaultExecutionDate() {
  return dayjs().format(ISO_DATE_FORMAT);
}

const InvoicesBasket: FC<Props> = ({ items, onCreateBundle }) => {
  const [bundleName, setBundleName] = useState("");
  const [requestedExecutionDate, setRequestedExecutionDate] = useState(defaultExecutionDate());
  const [selectedInvoiceKeys, setSelectedInvoiceKeys] = useState<string[]>([]);
  const aggregatedItems = Object.values(groupBy(items, (i) => invoiceKey(i.provider.id, i.invoice.invoiceNumber))).map(
    (items) => ({
      key: invoiceKey(items[0].provider.id, items[0].invoice.invoiceNumber),
      provider: items[0].provider,
      invoice: items[0].invoice,
      currency: items[0].currency,
      totalAmount: items
        .map((i) => i.invoice.amount)
        .reduce<Decimal>((prev, curr) => prev.plus(curr ?? 0), new Decimal(0)),
      costs: items.map(({ costId, bookingNumber }) => ({ costId, bookingNumber })),
    }),
  );
  // Derived from the current items, so invoices that left the basket after a refresh are never submitted
  const selectedItems = aggregatedItems.filter(({ key }) => selectedInvoiceKeys.includes(key));

  const handleCreateBundle = async () => {
    try {
      await onCreateBundle({
        name: bundleName,
        invoices: selectedItems.map(({ invoice, provider, costs }) => ({
          invoiceNumber: invoice.invoiceNumber,
          providerId: provider.id,
          costIds: costs.map(({ costId }) => costId),
        })),
        requestedExecutionDate,
      });
    } catch {
      // Failure is already reported to the user; keep the form filled in so it can be corrected and retried
      return;
    }
    setSelectedInvoiceKeys([]);
    setBundleName("");
    setRequestedExecutionDate(defaultExecutionDate());
  };

  return (
    <>
      <Typography variant="h6">Koszyk</Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Rezerwacje</TableCell>
            <TableCell>Dostawca</TableCell>
            <TableCell>Numer faktury</TableCell>
            <TableCell>Kwota</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                Brak faktur o statusie &#34;W koszyku&#34;
              </TableCell>
            </TableRow>
          )}
          {aggregatedItems.map(({ key, costs, provider, invoice, currency, totalAmount }) => (
            <TableRow key={key} data-testid="basket-invoice-row">
              <TableCell>
                <Checkbox
                  checked={selectedInvoiceKeys.includes(key)}
                  onChange={(e) =>
                    setSelectedInvoiceKeys((curr) =>
                      e.target.checked ? [...curr, key] : curr.filter((selectedKey) => selectedKey !== key),
                    )
                  }
                />
              </TableCell>
              <TableCell>{costs.map(({ bookingNumber }) => bookingNumber).join(", ")}</TableCell>
              <TableCell>{provider.name}</TableCell>
              <TableCell>{invoice.invoiceNumber}</TableCell>
              <TableCell>
                {totalAmount.toNumber()} {currency}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <TextField label="Nazwa paczki" value={bundleName} onChange={(e) => setBundleName(e.target.value)} />
        <DatePicker label="Data wykonania" value={requestedExecutionDate} onChange={setRequestedExecutionDate} />
        <AsyncLoadingButton
          onClick={handleCreateBundle}
          disabled={selectedItems.length === 0 || bundleName.length === 0}
        >
          Utwórz paczkę
        </AsyncLoadingButton>
      </Stack>
    </>
  );
};

export default InvoicesBasket;
